import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/badges";
import { toast } from "sonner";
import OverviewTab from "../components/project/OverviewTab";
import RequirementsTab from "../components/project/RequirementsTab";
import TasksTab from "../components/project/TasksTab";
import KanbanTab from "../components/project/KanbanTab";
import IssuesTab from "../components/project/IssuesTab";
import DiscussionsTab from "../components/project/DiscussionsTab";
import DocsTab from "../components/project/DocsTab";
import BomTab from "../components/project/BomTab";
import TaskModal from "../components/project/TaskModal";
import EditProjectDialog from "../components/project/EditProjectDialog";
import { LayoutDashboard, FileText, CheckSquare, Kanban, AlertCircle, MessagesSquare, Trash2, FolderOpen, Package } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [issues, setIssues] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");

  const reload = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data));
    api.get(`/projects/${id}/tasks`).then((r) => setTasks(r.data));
    api.get(`/projects/${id}/requirements`).then((r) => setRequirements(r.data));
    api.get(`/projects/${id}/issues`).then((r) => setIssues(r.data));
  }, [id]);

  useEffect(() => {
    reload();
    api.get("/users").then((r) => setUsers(r.data));
  }, [reload]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
    const taskParam = searchParams.get("task");
    if (taskParam) {
      setActiveTask(taskParam);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!project) return <p className="text-muted-foreground text-sm">Loading...</p>;

  const deleteProject = async () => {
    if (!window.confirm(`Delete project "${project.name}"? All its tasks, requirements, issues and history will be permanently removed.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (err) { toast.error(apiError(err)); }
  };

  const ctx = { project, tasks, requirements, issues, users, reload, openTask: setActiveTask };

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard, el: <OverviewTab {...ctx} /> },
    { id: "requirements", label: "Requirements", icon: FileText, el: <RequirementsTab {...ctx} /> },
    { id: "tasks", label: "Tasks", icon: CheckSquare, el: <TasksTab {...ctx} /> },
    { id: "kanban", label: "Board", icon: Kanban, el: <KanbanTab {...ctx} /> },
    { id: "issues", label: "Issues", icon: AlertCircle, el: <IssuesTab {...ctx} /> },
    { id: "bom", label: "BOM", icon: Package, el: <BomTab {...ctx} /> },
    { id: "docs", label: "Docs", icon: FolderOpen, el: <DocsTab {...ctx} /> },
    { id: "discussions", label: "Discussions", icon: MessagesSquare, el: <DiscussionsTab {...ctx} /> },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="project-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-primary">{project.key}</span>
            <StatusBadge status={project.status} />
            <span className="text-[10px] font-mono text-muted-foreground">{project.start_date} → {project.end_date}</span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.name}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{project.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(user?.role === "admin" || user?.role === "manager") && (
            <EditProjectDialog project={project} users={users} reload={reload} />
          )}
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" data-testid="delete-project-btn" onClick={deleteProject}
              className="text-rose-500 hover:text-rose-600 hover:border-rose-500/50">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete Project
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`} className="gap-1.5">
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-6">{t.el}</TabsContent>
        ))}
      </Tabs>

      {activeTask && (
        <TaskModal taskId={activeTask} tasks={tasks} requirements={requirements} users={users}
          onClose={() => setActiveTask(null)} onChanged={reload} />
      )}
    </div>
  );
}
